import { booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';
import { NavController } from 'providers/nav-controller';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { DestroyGuard } from 'services/browser-utils';
import { ExerciseDbService } from 'services/exercise-db.service';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';
import { TelemetryService } from 'services/telemetry.service';
import { LoggingService } from 'services/logging.service';
import { TextDbService } from 'services/text-db.service';
import { UserDb } from 'services/user-db.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { rewriteMarkdownHtml } from './markdown-rewrite';
import { playYoutubeNativeAsync } from 'services/cs-youtube-player';
import { LL } from 'i18n/i18n.service';
import { OnlineStateService } from 'services/online-state.service';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { resolveAnyExerciseByIdAsync } from 'services/exercise-resolve';

@Component({
  selector: 'cs-markdown',
  templateUrl: 'cs-markdown.html',
  host: { 'class': 'markdown' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsMarkdownComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  @Input() helpId: string = ''; // we deliberately don't put just "id" here, so it is easier to find all references in html sources
  @Input() textMarkdown: string;  // set this instead of helpId to show a markdown text directly
  @Input({ transform: booleanAttribute }) slim: boolean = false; // reduce margins, etc - useful on short texts or one-liners
  exerciseId: string;
  textHTML: SafeHtml;
  /// this is an _output_ property, it can be used by the parent component to _read_ the title from a text id and show it on the head of the page e.g
  @Input() textTitle: string;
  @Output() textTitleChange = new EventEmitter<string>();

  constructor(
    private navCtrl: NavController,
    private exerciseDb: ExerciseDbService,
    private textDb: TextDbService,
    private domSanitizer: DomSanitizer,
    private telemetry: TelemetryService,
    public logging: LoggingService,
    private userDb: UserDb,
    private cloudFunctions: CloudFunctionsService,
    private cd: ChangeDetectorRef,
    private onlineState: OnlineStateService,
    private toastCtrl: ToastController,
  ) { }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  ngOnInit() {
    void this.initializeAsync();
  }

  ngOnChanges(changes: SimpleChanges) {
    // if textMarkdown changes after initialization, re-render
    if (changes['textMarkdown'] && !changes['textMarkdown'].firstChange) {
      void this.initializeAsync();
    }
  }

  async initializeAsync() {
    try {
      if (this.helpId) {
        // TODO: this whole logic tree doesn't handle errors....
        let [, exerciseId] = this.helpId.match(/\@instructions\:(exercise\/.*)/) || ['', ''];
        if (exerciseId) {
          await this.loadExerciseInstructionsAsync(exerciseId);
          return;
        }

        let [, helpId] = this.helpId.match(/\@help\:(.*)/) || ['', ''];
        if (helpId) {
          await this.loadHelpAsync(helpId);
        }
      }
      if (this.textMarkdown) {
        await this.showTextAsync(undefined, this.textMarkdown);
      }
    } catch (err) {
      // a database that is shutting down or recovering is an expected transient state, not a
      // fault — the same reason EditExercisePage and ViewExercisePage skip reporting it. The
      // fallback content below still runs, so the reader is not left with a blank card.
      if (!isDatabaseUnavailable(err)) {
        this.logging.trackAbnormalError(`Missing help text for ${this.helpId} - ${err}`);
      }
      await this.loadHelpAsync('missing-help-text');
    }
  }

  // Re-callable: a parent can invoke initializeAsync() again to re-render after a LOCAL doc
  // write (e.g. an in-place coach-fork apply), which DocWatcher does not fire on — it only
  // reacts to cross-device sync pulls. The fresh read picks up the just-saved
  // description / video.
  async loadExerciseInstructionsAsync(exerciseId: string) {
    // withDocWatcherAsync, not a hand-written create/destroy: the read below can THROW — a
    // missing custom exercise, or a database that could not be read at all (see
    // `resolveAnyExerciseByIdAsync`) — and a `destroy()` on the line after it never runs
    // then, leaving the watcher registered for the rest of the session.
    let exercise = await this.exerciseDb.database.withDocWatcherAsync(async watcher =>
      resolveAnyExerciseByIdAsync(exerciseId, this.exerciseDb, this.userDb, watcher));
    if (this.destroyGuard.isDestroyed) return;
    if (exercise && (exercise.description || exercise.videoUrl)) {
      let description = exercise.description || '';
      // if video URL, prepend as Markdown image so it renders as an embed
      if (exercise.videoUrl) {
        description = `![Demo](${exercise.videoUrl})\n\n${description}`;
      }
      await this.showTextAsync(exercise.name, description);
    } else {
      // no description/video. clear any previously-rendered content first: this method is
      // re-callable (a parent re-renders after an apply), so an update that REMOVED the
      // description must not leave the old instructions on screen. then trigger CD.
      this.textHTML = this.domSanitizer.bypassSecurityTrustHtml('');
      this.cd.markForCheck();
    }
  }

  async loadHelpAsync(helpId: string) {
    let help = await this.textDb.getTextByHelpIdAsync(helpId);
    if (this.destroyGuard.isDestroyed) return;
    if (help && help.text) {
      await this.showTextAsync(help.title, help.text);
    } else {
      // No help text available, but we need to trigger change detection
      this.cd.markForCheck();
    }
  }

  async showTextAsync(header: string, bodyMD: string) {
    // only update title if provided (preserve parent-set header for preview mode)
    if (header != null) {
      this.textTitle = header;
      this.textTitleChange.emit(header);
    }

    // apply the image/link allow-list (see markdown-rewrite.ts)
    const rewrite = rewriteMarkdownHtml(marked(bodyMD, { async: false }), {
      useNativeYoutubePlayer: () => isCapacitorNative() && isPlatformIOS(),
      sharedUrlOrigin: getSharedUrlOrigin,
    });
    let htmlText = rewrite.html;
    const firebaseImageRefs = rewrite.firebaseImageRefs;

    // if the html calls a global tap handler, register it. the native YouTube
    // thumbnail needs its handler even when the text has no @help: link.
    if (rewrite.usesNativeYoutubeThumbnail) {
      window['csPlayNativeYoutube'] = (videoId: string) => {
        void this.openNativeYoutubePlayerAsync(videoId);
      };
    }
    if (rewrite.usesHelpLinks) {
      // (yes, this will stay forever, but not really a problem)
      window['openHelpLink'] = (helpId: string) => {
        void this.navCtrl.pushAsync(IHelpCardPage, { helpId: helpId });
      };
    }

    // resolve image paths to download URLs via server
    if (firebaseImageRefs.length > 0) {
      const promises = [];
      for (const imgRef of firebaseImageRefs) {
        const promise =
          this.cloudFunctions.storageGetDownloadUrlAsync({ path: decodeURIComponent(imgRef) })
            .then(result => {
              if (result.downloadUrl) {
                const target = new RegExp(imgRef, 'g');
                htmlText = htmlText.replace(target, result.downloadUrl);
              } else {
                // remove broken image tags so browser doesn't try to load the raw path
                this.logging.trackSilentProblem(`storageGetDownloadUrl returned no URL`, { imgRef, result });
                const target = new RegExp(`<img[^>]*src="${imgRef}"[^>]*>`, 'g');
                htmlText = htmlText.replace(target, '');
              }
            })
            .catch(e => {
              // remove broken image tags on error too
              const target = new RegExp(`<img[^>]*src="${imgRef}"[^>]*>`, 'g');
              htmlText = htmlText.replace(target, '');
              this.logging.trackSilentProblem(`storageGetDownloadUrl failed`, { imgRef, error: e?.message || e });
            });

        promises.push(promise);
      }

      await Promise.all(promises);
    }

    if (this.destroyGuard.isDestroyed) return;
    this.textHTML = this.domSanitizer.bypassSecurityTrustHtml(htmlText);
    this.cd.markForCheck();
  }

  // Opens the full-screen native YouTube player for a tapped thumbnail. This
  // only runs on the iOS native app, where buildYoutubeEmbedHtml emitted a
  // thumbnail instead of the inline iframe. The player service does the actual
  // work: the plugin call, the Safari-sheet fallback on a player error, and the
  // telemetry. Named differently from the service export (playYoutubeNativeAsync)
  // on purpose, so the two are not confused.
  private async openNativeYoutubePlayerAsync(videoId: string): Promise<void> {
    // F7 (#3991): a video needs the network. Without this guard the plugin call fails, and the
    // service's error path then opens the YouTube watch page in the Safari sheet — which offline
    // just shows a SECOND error. So skip both and say what is actually wrong.
    //
    // Only the iOS native thumbnail path goes through here. The web iframe stays unguarded on
    // purpose: it fails visibly on its own, and an overlay is not worth the machinery.
    // re-check before refusing: the cached state can sit stale for minutes (the foreground tick
    // re-checks an Offline belief, but only every six minutes), and refusing a user who is actually
    // online would be a regression on the old unguarded behavior
    if (!await this.onlineState.isLikelyOnlineOrRecheckAsync()) {
      this.toastCtrl.showToast({
        message: LL.app.videoOffline(),
        type: ToastType.WarningLight,
        duration: 4000,
      });
      return;
    }
    await playYoutubeNativeAsync(videoId, this.logging);
  }

  // this is used just to track telemetry when users unfold details on some help items
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // check if the click is on a <summary> inside a <details>
    if (target.tagName.toLowerCase() !== 'summary') {
      return;
    }
    const summaryElement = target as HTMLElement; // summary doesn't have a specific element class
    const detailsElement = summaryElement.closest('details') as HTMLDetailsElement;
    if (!detailsElement) {
      return;
    }

    // get the info about the item that was (un)folded
    let summaryText = 'empty';
    if (summaryElement && summaryElement.textContent) {
      summaryText = summaryElement.textContent.trim();
    }
    const action = detailsElement.open ? 'closing' : 'opening';

    // and report it to telemetry
    this.telemetry.trackEvent('help_details_toggling', { help_header: this.textTitle, summary_text: summaryText, toggle_action: action });
  }


}
